import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarBlank, Plus, Trash, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_COLORS = {
  pendiente: { bg: "rgba(245,158,11,0.12)", text: "#F59E0B" },
  confirmada: { bg: "rgba(94,106,210,0.15)", text: "#9EA9FF" },
  completada: { bg: "rgba(16,185,129,0.12)", text: "#10B981" },
  cancelada: { bg: "rgba(239,68,68,0.12)", text: "#EF4444" },
};

export default function CalendarPage() {
  const [items, setItems] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ contact_id: "", title: "", starts_at: "", duration_min: 30, notes: "" });

  const load = async () => {
    const [a, b] = await Promise.all([api.get("/appointments"), api.get("/contacts")]);
    setItems(a.data); setContacts(b.data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.contact_id || !form.title || !form.starts_at) return toast.error("Completa los campos");
    try {
      await api.post("/appointments", { ...form, duration_min: parseInt(form.duration_min) });
      toast.success("Cita creada");
      setOpen(false);
      setForm({ contact_id: "", title: "", starts_at: "", duration_min: 30, notes: "" });
      load();
    } catch {
      toast.error("Error al crear");
    }
  };

  const updateStatus = async (id, status) => {
    await api.patch(`/appointments/${id}`, { status });
    load();
  };

  const del = async (id) => {
    if (!confirm("Eliminar cita?")) return;
    await api.delete(`/appointments/${id}`);
    load();
  };

  const grouped = items.reduce((acc, a) => {
    const date = new Date(a.starts_at).toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    acc[date] = acc[date] || [];
    acc[date].push(a);
    return acc;
  }, {});

  return (
    <div className="p-8 max-w-5xl mx-auto fade-in">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#71717A] mb-2">Agenda</div>
          <h1 className="font-display text-4xl font-medium tracking-tighter">Citas y reservas</h1>
          <p className="text-sm text-[#A1A1AA] mt-1">{items.length} citas registradas</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#5E6AD2] hover:bg-[#4B55C3]" data-testid="new-appointment-button">
              <Plus size={16} className="mr-1.5" weight="bold" /> Nueva cita
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#121214] border-[#27272A] text-white">
            <DialogHeader><DialogTitle className="font-display tracking-tight">Nueva cita</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Contacto</Label>
                <Select value={form.contact_id} onValueChange={(v) => setForm({ ...form, contact_id: v })}>
                  <SelectTrigger className="bg-[#0A0A0C] border-[#27272A] mt-1" data-testid="appt-contact"><SelectValue placeholder="Selecciona contacto" /></SelectTrigger>
                  <SelectContent className="bg-[#121214] border-[#27272A] max-h-72">
                    {contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.phone})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Titulo</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Demo, consulta..." className="bg-[#0A0A0C] border-[#27272A] mt-1" data-testid="appt-title" /></div>
              <div><Label>Fecha y hora</Label><Input type="datetime-local" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} className="bg-[#0A0A0C] border-[#27272A] mt-1" data-testid="appt-datetime" /></div>
              <div><Label>Duracion (minutos)</Label><Input type="number" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} className="bg-[#0A0A0C] border-[#27272A] mt-1" /></div>
              <div><Label>Notas</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-[#0A0A0C] border-[#27272A] mt-1" /></div>
              <Button onClick={create} className="w-full bg-[#5E6AD2] hover:bg-[#4B55C3]" data-testid="save-appt-button">Guardar</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {Object.keys(grouped).length === 0 && (
        <div className="border border-dashed border-[#27272A] rounded-lg p-12 text-center text-[#71717A] text-sm">
          <CalendarBlank size={32} className="mx-auto mb-3 text-[#3F3F46]" />
          Sin citas. Agenda la primera.
        </div>
      )}

      {Object.entries(grouped).map(([date, list]) => (
        <div key={date} className="mb-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-3">{date}</div>
          <div className="space-y-2">
            {list.map((a) => (
              <div key={a.id} className="border border-[#27272A] bg-[#121214] rounded-lg p-4 flex items-center gap-4" data-testid={`appt-${a.id}`}>
                <div className="text-center w-16 shrink-0">
                  <div className="font-display text-2xl font-medium">{new Date(a.starts_at).getHours().toString().padStart(2, "0")}:{new Date(a.starts_at).getMinutes().toString().padStart(2, "0")}</div>
                  <div className="text-[10px] text-[#71717A] uppercase tracking-wider">{a.duration_min}m</div>
                </div>
                <div className="w-px h-10 bg-[#27272A]" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{a.title}</div>
                  <div className="text-xs text-[#A1A1AA]">{a.contact_name}</div>
                  {a.notes && <div className="text-xs text-[#71717A] mt-1 truncate">{a.notes}</div>}
                </div>
                <Select value={a.status} onValueChange={(v) => updateStatus(a.id, v)}>
                  <SelectTrigger className="w-36 bg-transparent border-[#27272A]" data-testid={`appt-status-${a.id}`}>
                    <span className="px-2 py-0.5 text-[10px] rounded uppercase tracking-wider font-medium" style={{ background: STATUS_COLORS[a.status]?.bg, color: STATUS_COLORS[a.status]?.text }}>{a.status}</span>
                  </SelectTrigger>
                  <SelectContent className="bg-[#121214] border-[#27272A]">
                    <SelectItem value="pendiente">Pendiente</SelectItem>
                    <SelectItem value="confirmada">Confirmada</SelectItem>
                    <SelectItem value="completada">Completada</SelectItem>
                    <SelectItem value="cancelada">Cancelada</SelectItem>
                  </SelectContent>
                </Select>
                <button onClick={() => del(a.id)} className="p-1.5 rounded hover:bg-[#27272A] text-[#71717A] hover:text-[#EF4444]" data-testid={`appt-delete-${a.id}`}>
                  <Trash size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
